import { useEffect, useState } from "react";
import { Megaphone, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHeader, EmptyState, Spinner } from "../components/common";
import { FormDialog } from "../components/FormDialog";
import { useResource } from "../hooks/useResource";
import { api, fmtDate, brl } from "../lib/api";

const STATUS = [{ value: "planejada", label: "Planejada" }, { value: "ativa", label: "Ativa" }, { value: "encerrada", label: "Encerrada" }];
const FIELDS = [
  { name: "name", label: "Nome da campanha", required: true, placeholder: "Ex.: Ofertas do mês" },
  { name: "description", label: "Descrição / objetivo", type: "textarea", rows: 3 },
  { name: "start_date", label: "Início", type: "date", half: true },
  { name: "end_date", label: "Fim", type: "date", half: true },
  { name: "status", label: "Status", type: "select", options: STATUS, default: "planejada", half: true },
  { name: "color", label: "Cor", type: "color", default: "#D4AF37", half: true },
];

export default function Campaigns() {
  const r = useResource("campaigns");
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [rel, setRel] = useState({ links: [], commissions: [] });
  useEffect(() => { Promise.all([api.get("/links"), api.get("/commissions")]).then(([l, c]) => setRel({ links: l.data, commissions: c.data })); }, []);

  const stats = (id) => {
    const links = rel.links.filter((l) => l.campaign_id === id);
    return { links: links.length, clicks: links.reduce((s, l) => s + l.clicks, 0), comm: rel.commissions.filter((c) => c.campaign_id === id && ["aprovado", "pago"].includes(c.status)).reduce((s, c) => s + c.amount, 0) };
  };

  return (
    <div>
      <PageHeader eyebrow="Estratégia" title="Campanhas" desc="Agrupe links e comissões por campanha e acompanhe o resultado."
        action={<Button className="btn-gold rounded-full h-11 px-5" onClick={() => setDlg({ open: true, item: null })} data-testid="campaign-add-button"><Plus className="h-4 w-4 mr-1" />Nova campanha</Button>} />
      {r.loading ? <Spinner /> : r.items.length === 0 ? <EmptyState icon={Megaphone} title="Nenhuma campanha" desc="Crie campanhas como 'Dia dos Pais' ou 'Ofertas 6.6'." /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {r.items.map((c) => {
            const s = stats(c.id);
            return (
              <div key={c.id} className="card-lux p-5 rise relative overflow-hidden" data-testid={`campaign-card-${c.id}`}>
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: c.color }} />
                <div className="flex justify-between items-start">
                  <div><div className="font-display text-lg font-bold">{c.name}</div><div className="text-[11px] text-zinc-500">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</div></div>
                  <span className="badge-gold capitalize">{c.status}</span>
                </div>
                {c.description && <p className="text-xs text-zinc-400 mt-3 line-clamp-3">{c.description}</p>}
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  {[["Links", s.links], ["Cliques", s.clicks], ["Comissão", brl(s.comm)]].map(([l, v]) => (
                    <div key={l} className="rounded-xl bg-zinc-950/60 border border-zinc-800 py-2"><div className="text-sm font-bold text-white">{v}</div><div className="text-[10px] text-zinc-500">{l}</div></div>
                  ))}
                </div>
                <div className="flex justify-end gap-1 mt-3">
                  <button className="p-2 text-zinc-500 hover:text-amber-400" onClick={() => setDlg({ open: true, item: c })} data-testid={`campaign-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                  <button className="p-2 text-zinc-500 hover:text-rose-400" onClick={() => r.remove(c.id)} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <FormDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} title={dlg.item ? "Editar campanha" : "Nova campanha"} fields={FIELDS}
        initial={dlg.item || { status: "planejada", color: "#D4AF37" }} testid="campaign-form" onSubmit={(b) => r.save(b, dlg.item?.id)} />
    </div>
  );
}
